import React from 'react';
import {
  STACK_COLORS,
  fmtCompact,
  getCurrencySymbol,
} from '../../../utils/currencyDashboardUtils';
import type { CurrencyEntry } from '../../../utils/currencyDashboardUtils';

interface CurrencyMixCardProps {
  entries: CurrencyEntry[];
  periodLabel?: string;
}

export const CurrencyMixCard: React.FC<CurrencyMixCardProps> = ({ entries, periodLabel }) => {
  // Show top 5 + "Other" if more exist
  const MAX_SHOW = 5;
  const top = entries.slice(0, MAX_SHOW);
  const rest = entries.slice(MAX_SHOW);
  const otherUsd = rest.reduce((s, e) => s + e.usdAmount, 0);
  const otherPct = rest.reduce((s, e) => s + e.pctShare, 0);

  const rows = rest.length > 0
    ? [...top, { currency: 'Other', usdAmount: otherUsd, originalAmount: 0, pctShare: otherPct }]
    : top;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      {/* Card header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-0.5 h-4 rounded-full bg-violet-500 flex-none" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
            Currency Mix
          </span>
        </div>
        {periodLabel && (
          <span className="text-[11px] text-slate-600">{periodLabel}</span>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        {rows.length === 0 && (
          <p className="text-sm text-slate-600 text-center py-4">No data</p>
        )}
        {rows.map((entry, idx) => {
          const color = idx < MAX_SHOW ? STACK_COLORS[idx % STACK_COLORS.length] : '#94A3B8';
          const sym = getCurrencySymbol(entry.currency);
          const isUsd = entry.currency === 'USD' || sym === '$';
          const displayAmount = isUsd ? entry.usdAmount : entry.originalAmount;
          return (
            <div key={entry.currency} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-none"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-[11px] font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded uppercase">
                    {entry.currency}
                  </span>
                </div>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[12px] font-semibold text-slate-700 tabular-nums">
                    {isUsd ? fmtCompact(displayAmount) : fmtCompact(displayAmount, sym)}
                  </span>
                  <span className="text-[11px] font-semibold text-slate-500 tabular-nums w-10 text-right">
                    {entry.pctShare.toFixed(1)}%
                  </span>
                </div>
              </div>
              {/* Progress bar */}
              <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, entry.pctShare).toFixed(1)}%`,
                    backgroundColor: color,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CurrencyMixCard;
